import { FC, useEffect, useMemo, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { json, Link, Outlet, redirect, useMatch, useNavigate, useSubmit } from 'react-router-dom'

// COMPONENTS
import { Column } from '@components/templates/column'
import Avatar from '@components/molecules/avatar'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import GroupBanner from '@components/organisims/group-banner'
import GroupProgressBar from '@components/molecules/group-progress-bar'
import PaymentMethodCell from '@components/organisims/payment-method-cell'
import WalletOptions from '@components/molecules/wallet-options'
import CreditBalance from '@components/molecules/credit-balance'

// ROUTES
import AddCardRoute from './add'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { createResponseObj, VirtualCardAPI } from '@apis'
import { priceFormatter } from '@utils/number-helper'
import { usePaymentMethods } from '@/hooks/tanstack/usePaymentMethods'
import { useGroupLoaderData, usePledgeOutletContext } from '../..'
import { createLimitedAction } from '@utils/actions'
import { isNonEmptyString, isObject } from '@utils/type-checkers'
import { queryClient } from '@/lib/queryClient'


const Component: FC<{}> = () => {
    const { user: clerkUser } = useUser()
    const submit = useSubmit()
    const context = usePledgeOutletContext()
    const group = useGroupLoaderData()
    const [{ pledge }, setState] = context
    const { paymentMethod } = pledge
    const [currentPaymentMethod, _] = useState(paymentMethod)
    const [useCreditBalance, setUseCreditBalance] = useState(pledge.useCredit || false)
    const creditAmount = useMemo(() => (clerkUser?.publicMetadata?.creditAmount) || 0, [clerkUser?.publicMetadata?.creditAmount])
    const showCreditBalance = false
    const isCreditBalanceApplied = showCreditBalance ? useCreditBalance : false

    // Determine if payment method is required
    // Payment method is NOT required only if: credit toggle is ON AND credit covers the full amount
    const requiresPaymentMethod = useMemo(() => {
        if ((pledge.amount || 0) === 0) return false
        if (isCreditBalanceApplied && (pledge.amount || 0) <= creditAmount) return false
        return true
    }, [isCreditBalanceApplied, pledge.amount, creditAmount])

    // When credit toggle is turned on with no amount entered, auto-fill with credit amount
    function handleCreditToggle(checked: boolean) {
        setUseCreditBalance(checked)
        setState((currentState) => ({
            ...currentState,
            pledge: {
                ...currentState.pledge,
                useCredit: checked,
                ...(checked && (!currentState.pledge.amount || currentState.pledge.amount === 0)
                    ? { amount: creditAmount }
                    : {}
                )
            }
        }))
    }

    const { data: paymentMethods = [], isPending } = usePaymentMethods(clerkUser?.id)
    const navigate = useNavigate()
    const isAdd = useMatch({ path: '/group/:groupId/pledge/pm/add' })

    const user = useMemo((): User => ({
        id: clerkUser?.id,
        givenName: clerkUser?.firstName,
        familyName: clerkUser?.lastName,
        imageUrl: clerkUser?.imageUrl,
    }), [clerkUser?.id, clerkUser?.firstName, clerkUser?.lastName, clerkUser?.imageUrl])

    // Handles reseting the state context after a payment method has been deleted from the users pm array.
    useEffect(() => {
        if (isPending) return
        if (paymentMethod && paymentMethod.type === 'credit_card' && !paymentMethods.find(({ token }) => token === paymentMethod.token))
            setState((currentState) => ({
                ...currentState,
                pledge: {
                    ...currentState.pledge,
                    paymentMethod: undefined,
                }
            }))
    }, [paymentMethods, paymentMethod, isPending])

    function onPaymentConfirmed(intentId: string, paymentMethod?: CreditCard | ApplePay | GooglePay) {
        const formData = new FormData()
        formData.append('isMerchantGroupWithNoOrganizer', `${group.type === 'merchant' && !group.organizer?.id}`)
        formData.append('pledges', JSON.stringify(group.pledges || []))
        formData.append('requestBody', JSON.stringify({
            ...pledge,
            user,
            transactionId: intentId,
            paymentMethod,
            useCredit: isCreditBalanceApplied
        }))

        submit(formData, { method: 'POST' })
    }

    function handlePMSelected(card: CreditCard) {
        // Toggle: if clicking the same card, deselect it
        if (paymentMethod?.type === 'credit_card' && paymentMethod.token === card.token) {
            setState((currentState) => ({
                ...currentState,
                pledge: {
                    ...currentState.pledge,
                    paymentMethod: undefined,
                }
            }))
        } else {
            setState((currentState) => ({
                ...currentState,
                pledge: {
                    ...currentState.pledge,
                    paymentMethod: card,
                }
            }))
        }
    }

    function handleAdd() {
        navigate('./add')
    }

    function handleBack() {
        navigate(-1)
    }

    return (<>
        <Column.Header.Start>
            {!isAdd && <a onClick={handleBack}>
                <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
            </a>}
        </Column.Header.Start>
        <div className="flex flex-col gap-4">
            {/* Titles */}
            <div className='group-banner-section' style={{ marginBottom: '1rem' }}>
                <GroupBanner group={group} isGroupMember={true} />
                <h4 style={{ color: 'var(--splitcard-gray-500)', textAlign: 'center' }}>{(group.type === 'merchant' && group.organizer === null) ? group.merchant?.name || 'Someone' : (`${group.organizer?.givenName || ''} ${group.organizer?.familyName || ''}`.trim() || 'Someone')} is Inviting You To Contribute!</h4>
            </div>
            <GroupProgressBar goal={group.targetAmount || 0 / 100} pledge={((group.collectedAmount || 0) / 100) + ((pledge?.amount || 0) / 100)} />
            {isAdd && <h3 id="add-card-title" style={{ textAlign: 'center' }}>Add New Payment Method</h3>}
            {(!isAdd) && (<>
                <Card style={{ marginBottom: '1rem' }}>
                    <div className="flex flex-row items-center" style={{ gap: 'inherit' }}>
                        <Avatar user={user} style={{ height: '3.125rem', width: '3.125rem' }} />
                        <div className="flex flex-row items-center justify-between flex-1">
                            <div className='flexcolumn'>
                                <p className="text-body-2 text-gray-500 text-left">Your contribution</p>
                                <h2>{priceFormatter.format((pledge.amount || 0) / 100)}</h2>
                            </div>
                            {group.type === 'open' && <p className="text-body-2"><Link to="../../contribute">Edit</Link></p>}
                        </div>
                    </div>
                </Card>
                <h2 style={{ textAlign: 'center' }}>Choose Payment Method</h2>
            </>)}
            {(isAdd || currentPaymentMethod === undefined || currentPaymentMethod?.type === 'credit_card') ? null :
                <PaymentMethodCell
                    selected={paymentMethod?.type !== 'credit_card'}
                    paymentMethod={currentPaymentMethod}
                />
            }
            {!isAdd && showCreditBalance && creditAmount > 0 && (
                <CreditBalance
                    creditAmount={creditAmount}
                    isApplied={useCreditBalance}
                    onToggle={handleCreditToggle}
                />
            )}
            {isAdd ? null : paymentMethods.map((method, index) => (
                <PaymentMethodCell key={`${method.token}-${index}`}
                    selected={paymentMethod?.type === 'credit_card' && method.token === paymentMethod?.token}
                    onClick={requiresPaymentMethod ? () => handlePMSelected(method) : undefined}
                    paymentMethod={method}
                    removable
                    style={{ opacity: requiresPaymentMethod ? 1 : 0.5, pointerEvents: requiresPaymentMethod ? 'auto' : 'none' }}
                />
            ))}
            <PaymentMethodCell type={isAdd ? 'empty' : 'add'} onClick={!isAdd ? handleAdd : undefined}>
                <Outlet context={context} />
            </PaymentMethodCell>
            {(!isAdd) &&
                <WalletOptions
                    groupId={group.id}
                    amount={isCreditBalanceApplied ? Math.max(0, pledge.amount - creditAmount) : pledge.amount}
                    onConfirm={onPaymentConfirmed}
                    style={{ opacity: requiresPaymentMethod ? 1 : 0.5, pointerEvents: requiresPaymentMethod ? 'auto' : 'none' }}
                />
            }
        </div>
        {isAdd ? null : (
            <Column.Footer sticky>
                <Button disabled={requiresPaymentMethod && !paymentMethod} to="../review">Next</Button>
            </Column.Footer>
        )}
    </>)
}



const action = createLimitedAction({
    limit: 'joinGroups',
    getRedirectPath: ({ params }) => `/group/${params.groupId}`,
}, async ({ request, params }) => {
    try {
        const formData = await request.formData()
        const groupId = params.groupId
        const isMerchantGroupWithNoOrganizer = formData.get('isMerchantGroupWithNoOrganizer') === 'true'
        const pledges = JSON.parse(`${formData.get('pledges') || '[]'}`)
        const requestBody = JSON.parse(`${formData.get("requestBody")}`)

        console.log("Pledge Data:", requestBody)
        if (!isNonEmptyString(groupId) || !isPledge(requestBody)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify(requestBody)}`
        })

        // Generate a setupIntent if one wasn't provided.
        var transactionId = requestBody.transactionId
        if (!isNonEmptyString(transactionId)) {
            throw createResponseObj({
                status: 'failed', type: 'local', name: 'missing-resources',
                message: `Missing required resources. ${JSON.stringify(requestBody)}`
            })
        }

        if (isMerchantGroupWithNoOrganizer) await VirtualCardAPI.UpdatePledgeRequest.fetch(groupId, { ...requestBody, id: pledges?.[0]?.id, transactionId, useCredit: requestBody.useCredit })
        else if (requestBody.id) await VirtualCardAPI.UpdatePledgeRequest.fetch(groupId, { ...requestBody, id: requestBody.id, transactionId, useCredit: requestBody.useCredit })
        else await VirtualCardAPI.AddPledgeRequest.fetch(groupId, { ...requestBody, transactionId, useCredit: requestBody.useCredit })
        queryClient.invalidateQueries({ queryKey: ['user-groups'] })

        return redirect(`/group/${groupId}`)
    } catch (error) {
        console.error(error)
        return json(error, { status: 500 })
    }
})

// ToDo: Pledge Validation Schema
const isPledge = (value: unknown): value is Pledge => (
    isObject(value)
)

const PMRoute: RouteObject = {
    path: "pm",
    Component,
    action,
    children: [
        AddCardRoute,
    ],
}

export { PMRoute }
